import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { getProductProfile, calculateSpineWidthMm } from '@fotoboek/domain';
import { useProjectStore } from '../../state/projectStore';
import { StaticPageRenderer } from '../../components/preview/StaticPageRenderer';
import { Spinner } from '../../components/ui/Spinner';
import type { SceneItem } from '../../components/editor/canvas/scene';
import { t } from '../../i18n';
import './preview-page.css';

export default function PreviewPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = useProjectStore((s) => s.project);
  const assetsRuntime = useProjectStore((s) => s.assetsRuntime);
  const status = useProjectStore((s) => s.status);
  const loadProject = useProjectStore((s) => s.loadProject);
  const reset = useProjectStore((s) => s.reset);
  const [index, setIndex] = useState(-1); // -1 = cover

  useEffect(() => {
    if (projectId) void loadProject(projectId);
    return () => reset();
  }, [projectId, loadProject, reset]);

  if (status !== 'ready' || !project) {
    return (
      <div className="preview-loading">
        <Spinner size={28} />
      </div>
    );
  }

  const profile = getProductProfile(project.productProfileId);
  const total = project.pages.length;

  const coverItems: SceneItem[] = (() => {
    const spineW = calculateSpineWidthMm(
      project.pages.length,
      project.settings.paperStockId,
      profile,
    );
    return [
      {
        target: { scope: 'cover', panel: 'back' },
        originMm: { x: 0, y: 0 },
        sizeMm: profile.pageSizeMm,
        bleedMm: 0,
        safeMarginMm: 0,
        backgroundColor: project.cover.panels.back.background.color,
        elements: project.cover.panels.back.elements,
      },
      {
        target: { scope: 'cover', panel: 'spine' },
        originMm: { x: 0, y: 0 },
        sizeMm: { width: spineW, height: profile.pageSizeMm.height },
        bleedMm: 0,
        safeMarginMm: 0,
        backgroundColor: project.cover.panels.spine.background.color,
        elements: project.cover.panels.spine.elements,
      },
      {
        target: { scope: 'cover', panel: 'front' },
        originMm: { x: 0, y: 0 },
        sizeMm: profile.pageSizeMm,
        bleedMm: 0,
        safeMarginMm: 0,
        backgroundColor: project.cover.panels.front.background.color,
        elements: project.cover.panels.front.elements,
      },
    ];
  })();

  const currentPageItem: SceneItem | null =
    index >= 0
      ? (() => {
          const page = project.pages.find((p) => p.order === index);
          if (!page) return null;
          return {
            target: { scope: 'page', pageId: page.id },
            originMm: { x: 0, y: 0 },
            sizeMm: profile.pageSizeMm,
            bleedMm: 0,
            safeMarginMm: 0,
            backgroundColor: page.background.color,
            elements: page.elements,
          };
        })()
      : null;

  return (
    <div className="preview-page">
      <div className="preview-topbar">
        <Link to={`/editor/${project.id}`} className="preview-back">
          <ArrowLeft size={16} />
          {t('preview.back')}
        </Link>
        <span className="preview-title">{project.title}</span>
        <span className="preview-page-indicator">
          {index === -1 ? t('preview.cover') : t('preview.pageOf', { current: index + 1, total })}
        </span>
      </div>

      <div className="preview-stage">
        <button
          className="preview-nav"
          onClick={() => setIndex((i) => Math.max(-1, i - 1))}
          disabled={index === -1}
        >
          <ChevronLeft size={22} />
        </button>

        <div className="preview-book">
          {index === -1 ? (
            <div className="preview-cover-row">
              {coverItems.map((item) => (
                <StaticPageRenderer
                  key={`${item.target.scope}-${'panel' in item.target ? item.target.panel : ''}`}
                  item={item}
                  assetsRuntime={assetsRuntime}
                />
              ))}
            </div>
          ) : currentPageItem ? (
            <StaticPageRenderer item={currentPageItem} assetsRuntime={assetsRuntime} />
          ) : null}
        </div>

        <button
          className="preview-nav"
          onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
          disabled={index >= total - 1}
        >
          <ChevronRight size={22} />
        </button>
      </div>
    </div>
  );
}
